import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getZodiacRoomCounts, onZodiacRoomCounts, socket } from '../services/socket';
import {
  applyZodiacRoomCountSnapshot,
  CANONICAL_ZODIAC_ROOM_IDS,
  zodiacRoomIdForSign,
} from '../utils/zodiacRoomCounts';

const signos = [
  { key: 'capricornio', translationKey: 'capricorn', emoji: '♑' },
  { key: 'acuario', translationKey: 'aquarius', emoji: '♒' },
  { key: 'piscis', translationKey: 'pisces', emoji: '♓' },
  { key: 'aries', translationKey: 'aries', emoji: '♈' },
  { key: 'tauro', translationKey: 'taurus', emoji: '♉' },
  { key: 'geminis', translationKey: 'gemini', emoji: '♊' },
  { key: 'cancer', translationKey: 'cancer', emoji: '♋' },
  { key: 'leo', translationKey: 'leo', emoji: '♌' },
  { key: 'virgo', translationKey: 'virgo', emoji: '♍' },
  { key: 'libra', translationKey: 'libra', emoji: '♎' },
  { key: 'escorpio', translationKey: 'scorpio', emoji: '♏' },
  { key: 'sagitario', translationKey: 'sagittarius', emoji: '♐' },
];

function slugifySign(name) {
  return String(name)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-');
}

const ZodiacRoomsScreen = () => {
  const { t } = useTranslation(['rooms', 'common', 'home']);
  const navigate = useNavigate();
  const [countSnapshot, setCountSnapshot] = useState(() => ({
    counts: Object.fromEntries(CANONICAL_ZODIAC_ROOM_IDS.map((room) => [room, 0])),
    snapshotAt: 0,
  }));
  const applySnapshot = useCallback((snapshot) => {
    setCountSnapshot((current) => {
      const next = applyZodiacRoomCountSnapshot(current, snapshot);
      return next;
    });
  }, []);

  const requestSnapshot = useCallback(() => {
    void getZodiacRoomCounts(applySnapshot);
  }, [applySnapshot]);

  useEffect(() => {
    const unsubscribe = onZodiacRoomCounts(applySnapshot);
    const onConnect = () => requestSnapshot();
    const onReconnect = () => requestSnapshot();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') requestSnapshot();
    };

    requestSnapshot();
    if (socket.connected) requestSnapshot();
    socket.on('connect', onConnect);
    socket.io.on('reconnect', onReconnect);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      unsubscribe();
      socket.off('connect', onConnect);
      socket.io.off('reconnect', onReconnect);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [applySnapshot, requestSnapshot]);

  const counts = countSnapshot.counts;

  const handleEnterRoom = (sign) => {
    navigate(`/rooms/${encodeURIComponent(slugifySign(sign))}`);
  };

  const handleGoBack = () => {
    navigate('/home', { replace: true });
  };

  return (
    <>
      <style>{`
        @keyframes rooms-star-drift {
          from { background-position: 20px 14px, 88px 46px, center; }
          to { background-position: 20px 136px, 88px 230px, center; }
        }

        @keyframes rooms-wheel-spin {
          from { rotate: 0deg; }
          to { rotate: 360deg; }
        }

        @keyframes rooms-tile-shine {
          0% { transform: translateX(-145%) rotate(12deg); opacity: 0; }
          44% { opacity: 0.52; }
          100% { transform: translateX(145%) rotate(12deg); opacity: 0; }
        }

        #roomsRoot::before {
          content: "";
          position: fixed;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          background-image:
            radial-gradient(circle, rgba(255,226,138,0.72) 0 1px, transparent 1.45px),
            radial-gradient(circle, rgba(255,255,255,0.38) 0 1px, transparent 1.35px),
            linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.56));
          background-size: 112px 112px, 174px 174px, 100% 100%;
          background-position: 20px 14px, 88px 46px, center;
          opacity: 0.44;
          animation: rooms-star-drift 18s linear infinite;
        }

        #roomsRoot::after {
          content: "";
          position: fixed;
          top: -15%;
          left: 50%;
          width: min(92vw, 920px);
          height: min(92vw, 920px);
          z-index: 0;
          transform: translateX(-50%);
          pointer-events: none;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(174,65,255,0.12) 0%, rgba(218,165,32,0.055) 34%, transparent 68%);
        }

        #roomsRoot .roomsWheel {
          position: fixed;
          top: 50%;
          left: 50%;
          width: min(92vmin, 820px);
          height: min(92vmin, 820px);
          translate: -50% -50%;
          border-radius: 50%;
          background-image: url("/logo/Zodiacus.jpeg");
          background-repeat: no-repeat;
          background-position: center center;
          background-size: contain;
          opacity: 0.20;
          filter: brightness(1.16) saturate(1.08) drop-shadow(0 0 44px rgba(154,47,255,0.20));
          z-index: 0;
          pointer-events: none;
          will-change: transform;
          transform-origin: 50% 50%;
          animation: rooms-wheel-spin 180s linear infinite;
          -webkit-mask-image: radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 94%, rgba(0,0,0,0) 100%);
          mask-image: radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 94%, rgba(0,0,0,0) 100%);
        }

        #roomsRoot .roomsCard::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            radial-gradient(circle at 18% 10%, rgba(218,165,32,0.025), transparent 24%),
            radial-gradient(circle at 88% 62%, rgba(188,69,255,0.035), transparent 34%);
          z-index: 0;
        }

        #roomsRoot .roomsCard > * {
          position: relative;
          z-index: 2;
        }

        #roomsRoot .roomsDivider span {
          height: 1px;
          flex: 1;
          max-width: 170px;
          background: linear-gradient(90deg, transparent, rgba(255,217,121,0.70), rgba(232,95,255,0.22), transparent);
        }

        #roomsRoot .roomsGrid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
          align-items: stretch;
        }

        #roomsRoot .roomTile {
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }

        #roomsRoot .roomTile::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          pointer-events: none;
          background:
            radial-gradient(circle at 20% 18%, rgba(255,216,118,0.10), transparent 24%),
            radial-gradient(circle at 82% 86%, rgba(188,69,255,0.12), transparent 34%);
          opacity: 0.72;
        }

        #roomsRoot .roomTile::after {
          content: "";
          position: absolute;
          top: -26%;
          bottom: -26%;
          left: 0;
          width: 42%;
          pointer-events: none;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
          animation: rooms-tile-shine 6s ease-in-out infinite;
        }

        #roomsRoot .roomTile:hover {
          transform: translateY(-3px);
          box-shadow: 0 18px 38px rgba(0,0,0,0.34), 0 0 28px rgba(184,63,255,0.22);
          border-color: rgba(232,95,255,0.72) !important;
          filter: saturate(1.08);
        }

        #roomsRoot .roomTile:active {
          transform: scale(0.985);
        }

        #roomsRoot .roomsBackTop {
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
          user-select: none;
        }

        #roomsRoot .roomsBackTop:active {
          transform: scale(0.97);
          filter: brightness(0.98);
        }

        @media (min-width: 920px) {
          #roomsRoot .roomsGrid {
            grid-template-columns: repeat(2, minmax(280px, 1fr));
            gap: 18px;
          }

          #roomsRoot .roomsCard {
            max-width: 980px !important;
          }

          #roomsRoot .roomTile {
            min-height: 126px !important;
            padding: 18px 20px !important;
          }
        }

        @media (max-width: 520px) {
          #roomsRoot {
            min-height: 100svh !important;
            max-height: 100svh !important;
            padding: calc(env(safe-area-inset-top, 0px) + 50px) 10px calc(env(safe-area-inset-bottom, 0px) + 8px) !important;
            display: flex !important;
            align-items: stretch !important;
            justify-content: center !important;
            overflow: hidden !important;
          }

          #roomsRoot > div[style] {
            width: 100% !important;
          }

          #roomsRoot .roomsWheel {
            top: 50% !important;
            left: 50% !important;
            width: min(94vw, 80svh) !important;
            height: min(94vw, 80svh) !important;
            opacity: 0.24 !important;
            translate: -50% -50% !important;
            transform-origin: 50% 50% !important;
            -webkit-mask-image: radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 94%, rgba(0,0,0,0) 100%) !important;
            mask-image: radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 94%, rgba(0,0,0,0) 100%) !important;
          }

          #roomsRoot .roomsCard {
            width: 100% !important;
            max-width: none !important;
            min-height: calc(100svh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 58px) !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: flex-start !important;
            padding: 14px 12px 10px !important;
            border-radius: 24px !important;
            max-height: none !important;
            background: linear-gradient(180deg, rgba(20,5,42,0.14), rgba(8,2,24,0.22)) !important;
            backdrop-filter: blur(3px) !important;
            -webkit-backdrop-filter: blur(3px) !important;
          }

          #roomsRoot .roomsTitle {
            font-size: clamp(1.74rem, 8.6vw, 2.26rem) !important;
            line-height: 0.92 !important;
            padding: 0 22px !important;
          }

          #roomsRoot .roomsDivider {
            margin: 6px auto 6px !important;
          }

          #roomsRoot .roomsSubtitle {
            font-size: 0.68rem !important;
            line-height: 1.10 !important;
            margin-bottom: 7px !important;
          }

          #roomsRoot .roomsGrid {
            flex: 1 1 auto !important;
            grid-template-rows: repeat(6, minmax(0, 1fr)) !important;
            align-content: stretch !important;
            gap: 7px !important;
          }

          #roomsRoot .roomTile {
            min-height: 0 !important;
            height: 100% !important;
            padding: 7px 6px 12px !important;
            border-radius: 16px !important;
            grid-template-columns: 1fr !important;
            grid-template-areas: "badge" "name" "count" !important;
            gap: 2px !important;
            justify-items: center !important;
            align-content: center !important;
            text-align: center !important;
          }

          #roomsRoot .roomBadge {
            width: 30px !important;
            height: 30px !important;
            font-size: 17px !important;
          }

          #roomsRoot .roomName {
            font-size: 0.72rem !important;
            line-height: 1 !important;
          }

          #roomsRoot .roomCount {
            font-size: 0.58rem !important;
            line-height: 1 !important;
            gap: 3px !important;
          }

          #roomsRoot .roomArrow {
            position: absolute !important;
            right: 50% !important;
            transform: translateX(50%) !important;
            bottom: 1px !important;
            font-size: 0.72rem !important;
          }

          #roomsRoot .roomsBackTop {
            top: calc(env(safe-area-inset-top, 0px) + 12px) !important;
            left: 14px !important;
            width: 44px !important;
            height: 44px !important;
            min-height: 44px !important;
            padding: 0 !important;
            gap: 0 !important;
            z-index: 30 !important;
          }

          #roomsRoot .roomsBackTop span:last-child {
            display: none !important;
          }

          #roomsRoot .roomsBackTop span:first-child {
            width: 34px !important;
            height: 34px !important;
            font-size: 20px !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          #roomsRoot::before,
          #roomsRoot .roomsWheel,
          #roomsRoot .roomTile::after {
            animation: none !important;
          }
        }
      `}</style>

      <div
        id="roomsRoot"
        style={{
          minHeight: '100svh',
          position: 'relative',
          isolation: 'isolate',
          overflowX: 'hidden',
          overflowY: 'hidden',
          background: '#05010F',
          padding: '76px 30px 28px',
          boxSizing: 'border-box',
        }}
      >

        <div className="roomsWheel" aria-hidden="true" />

        <button type="button" className="roomsBackTop" onClick={handleGoBack} style={styles.backTopBtn} aria-label={t('roomsList.backToHome')}>
          <span style={styles.backTopIcon} aria-hidden="true">←</span>
          <span>{t('common:actions.back')}</span>
        </button>

        <div style={{ ...styles.container, position: 'relative', zIndex: 1 }}>
          <div className="roomsCard" style={styles.card}>
            <h2 className="roomsTitle" style={styles.title}>{t('roomsList.title')}</h2>
            <div className="roomsDivider" aria-hidden="true" style={styles.divider}>
              <span />
              <i>✦</i>
              <span />
            </div>
            <p className="roomsSubtitle" style={styles.subtitle}>
              {t('roomsList.subtitlePrimary')}<br />
              {t('roomsList.subtitleSecondary')}
            </p>

            <div className="roomsGrid" style={styles.grid}>
              {signos.map(({ key, translationKey, emoji }) => {
                const roomId = zodiacRoomIdForSign(key);
                const c = counts[roomId] ?? 0;
                const displayName = t(`home:zodiacSigns.${translationKey}`);
                const connectedLabel = t('roomsList.connectedCount', { count: c });

                return (
                  <button
                    key={key}
                    className="roomTile"
                    style={{
                      ...styles.roomTile,
                      background:
                        'radial-gradient(circle at 18% 12%, rgba(255,232,160,0.18) 0%, rgba(255,255,255,0.06) 38%, rgba(0,0,0,0) 68%),' +
                        'linear-gradient(135deg, rgba(75,0,130,0.30), rgba(38,0,86,0.32) 55%, rgba(18,0,44,0.42))',
                      border: '1px solid rgba(218,165,32,0.32)',
                      boxShadow: '0 10px 24px rgba(0,0,0,0.16), 0 0 18px rgba(218,165,32,0.08)',
                    }}
                    onClick={() => handleEnterRoom(key)}
                    aria-label={t('roomsList.enterRoom', {
                      sign: displayName,
                      connected: connectedLabel,
                    })}
                  >
                    <div className="roomBadgeWrap" style={styles.badgeWrap} aria-hidden="true">
                      <div className="roomBadge" style={styles.badge}>{emoji}</div>
                    </div>
                    <div className="roomName" style={styles.roomName}>{displayName}</div>
                    <div className="roomCount" style={styles.count}>
                      <span aria-hidden="true">👥</span> {connectedLabel}
                    </div>
                    <div className="roomArrow" style={styles.arrow} aria-hidden="true">›</div>
                  </button>
                );
              })}
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  container: {
    minHeight: 'auto',
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: 0,
  },
  card: {
    width: '100%',
    maxWidth: 900,
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(180deg, rgba(20,5,42,0.28), rgba(8,2,24,0.38))',
    color: '#FFF6D8',
    borderRadius: 34,
    padding: '50px 34px 34px',
    boxShadow: '0 22px 64px rgba(0,0,0,0.32), 0 0 30px rgba(171,54,255,0.14), inset 0 1px 0 rgba(255,255,255,0.10)',
    border: '1px solid rgba(232,95,255,0.24)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  },
  title: {
    margin: 0,
    textAlign: 'center',
    color: '#FFD979',
    fontWeight: 600,
    fontSize: 'clamp(2.45rem, 6vw, 4.15rem)',
    letterSpacing: '-0.035em',
    lineHeight: 0.98,
    textShadow: '0 0 26px rgba(218,165,32,0.20)',
    fontFamily: "'Cinzel', 'Cormorant Garamond', serif",
  },
  divider: {
    width: 'min(100%, 520px)',
    margin: '14px auto 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    color: '#FFD979',
  },
  subtitle: {
    textAlign: 'center',
    margin: '0 auto 30px',
    maxWidth: 650,
    color: 'rgba(255,255,255,0.86)',
    fontWeight: 650,
    fontSize: '1.08rem',
    lineHeight: 1.42,
    textShadow: '0 1px 14px rgba(0,0,0,0.40)',
  },
  grid: {},
  roomTile: {
    minHeight: 120,
    display: 'grid',
    gridTemplateColumns: '72px minmax(0, 1fr) 28px',
    gridTemplateRows: 'auto auto',
    gridTemplateAreas: '"badge name arrow" "badge count arrow"',
    alignItems: 'center',
    gap: '4px 14px',
    borderRadius: 22,
    padding: '18px 18px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'transform 150ms ease, box-shadow 200ms ease, filter 200ms ease, border-color 200ms ease',
    color: '#FFF6D8',
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, rgba(45,7,85,0.34), rgba(12,2,34,0.48))',
    border: '1px solid rgba(217,93,255,0.28)',
    boxShadow: '0 12px 28px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.06)',
  },
  badgeWrap: {
    gridArea: 'badge',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 2,
  },
  badge: {
    width: 58,
    height: 58,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'radial-gradient(circle at 35% 24%, rgba(190,80,255,0.62), rgba(35,6,70,0.90) 64%, rgba(5,1,18,0.96) 100%)',
    border: '1px solid rgba(255,218,116,0.78)',
    boxShadow: '0 0 24px rgba(190,80,255,0.42), 0 0 18px rgba(218,165,32,0.16), inset 0 0 14px rgba(255,218,116,0.08)',
    fontSize: 32,
    lineHeight: 1,
    color: '#FFD979',
    textShadow: '0 0 16px rgba(218,165,32,0.42)',
  },
  roomName: {
    gridArea: 'name',
    position: 'relative',
    zIndex: 2,
    fontWeight: 900,
    color: '#FFD979',
    letterSpacing: '-0.02em',
    fontSize: '1.18rem',
    lineHeight: 1.12,
    textShadow: '0 0 10px rgba(218,165,32,0.28), 0 2px 12px rgba(0,0,0,0.44)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  count: {
    gridArea: 'count',
    position: 'relative',
    zIndex: 2,
    fontSize: 13,
    fontWeight: 800,
    color: 'rgba(255,255,255,0.78)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    textShadow: '0 1px 12px rgba(0,0,0,0.40)',
  },
  arrow: {
    gridArea: 'arrow',
    position: 'relative',
    zIndex: 2,
    justifySelf: 'end',
    fontSize: 42,
    lineHeight: 1,
    color: '#D45CFF',
    textShadow: '0 0 18px rgba(212,92,255,0.55)',
    fontWeight: 300,
  },
  backTopBtn: {
    position: 'fixed',
    top: 'calc(env(safe-area-inset-top, 0px) + 18px)',
    left: 30,
    zIndex: 20,
    minHeight: 52,
    padding: '0 18px 0 10px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    borderRadius: 999,
    border: '1px solid rgba(232,95,255,0.62)',
    background: 'linear-gradient(180deg, rgba(18,5,42,0.88), rgba(8,2,24,0.94))',
    color: '#FFE08B',
    fontWeight: 900,
    cursor: 'pointer',
    boxShadow: '0 0 24px rgba(190,60,255,0.30), 0 12px 30px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.08)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
  },
  backTopIcon: {
    width: 34,
    height: 34,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'radial-gradient(circle at 35% 24%, rgba(255,224,130,0.24), rgba(160,68,255,0.12) 68%, rgba(5,1,18,0.34) 100%)',
    border: '1px solid rgba(255,218,116,0.42)',
    color: '#FFE98A',
    boxShadow: '0 0 16px rgba(218,165,32,0.20), inset 0 0 10px rgba(255,218,116,0.08)',
    fontSize: 22,
    lineHeight: 1,
  },
};

export default ZodiacRoomsScreen;
